"use client";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/shadcn/dialog";
import { cn } from "@/utils/cn";

import { CommandEmpty as CommandEmptyPrimitive, CommandGroup as CommandGroupPrimitive, CommandInput as CommandInputPrimitive, CommandItem as CommandItemPrimitive, CommandList as CommandListPrimitive, Command as CommandPrimitive, CommandSeparator as CommandSeparatorPrimitive } from "cmdk";
import * as React from "react";

import { memo } from "react";

/**
 * Command — shadcn primitive migrated to class-selector + co-located CSS.
 *
 * REFERENCE PATTERN: class-selector + co-located CSS (see shadcn-authoring.md
 * commit b35c0a615). cmdk wrapper exposing a search palette / command-menu
 * UI surface.
 *
 * 8 sub-components total; all styled identities (no verbatim re-exports):
 *   .dw-command           — root flex column container (Command)
 *   .dw-command-dialog    — modifier shape on DialogContent (CommandDialog)
 *   .dw-command-input     — input + wrapper row (CommandInput)
 *   .dw-command-list      — scrollable results container (CommandList)
 *   .dw-command-empty     — empty-state placeholder (CommandEmpty)
 *   .dw-command-group     — labeled group of items (CommandGroup)
 *   .dw-command-separator — divider line between groups (CommandSeparator)
 *   .dw-command-item      — selectable result row (CommandItem)
 *   .dw-command-shortcut  — right-aligned key-hint span (CommandShortcut)
 *
 * Internal sub-element on CommandInput:
 *   .dw-command-input__wrapper — flex row containing the input + sentinel
 *
 * cmdk emits its own data-attributes that styling layers on:
 *   [data-disabled="true"]  — disabled item (pointer-events:none + opacity)
 *   [data-selected="true"]  — keyboard-/cursor-highlighted item (muted bg)
 *
 * cmdk also renders structural wrappers with `[cmdk-*]` attributes that
 * we style via descendant selectors:
 *   [cmdk-group-heading] — group label inside CommandGroup
 *   [cmdk-input-wrapper] — wrapper rendered around CommandInput
 *
 * Public API preserved EXACTLY: every named export, every forwardRef +
 * displayName, the cmdk-input-wrapper attribute on CommandInput's outer
 * div, the type-import shape, and the CommandDialog composition that
 * wraps DialogContent + DialogTitle preserved verbatim. CommandShortcut
 * remains a plain function component (no forwardRef) — pre-migration
 * shape preserved.
 *
 * No variant axes. No cva. No commandVariants export ever existed
 * (verified via grep).
 *
 * Source: docs/implementation-blueprint.md §8.3 (class-selector pattern);
 *         .claude/agent-prompts/shadcn-authoring.md (binding template, commit b35c0a615);
 *         .claude/agent-prompts/atom-authoring-class-selector.md (shared rules);
 *         src/components/ui/shadcn/dialog.tsx (multi-export precedent, commit ec0e2a62a);
 *         src/components/ui/shadcn/select.tsx (cmdk-shape precedent — Radix data-attrs, commit 4af955690).
 */

const Command = React.forwardRef<React.ComponentRef<typeof CommandPrimitive>, React.ComponentPropsWithoutRef<typeof CommandPrimitive>>(({ className, ...props }, ref) => <CommandPrimitive ref={ref} className={cn("dw-command", className)} data-component="Command" data-location="src/components/ui/shadcn/command.tsx" {...props} />);
Command.displayName = CommandPrimitive?.displayName || "Command";

const CommandDialog = memo(
    function CommandDialog({ children, ...props }: React.ComponentProps<typeof Dialog>) {
        return (
            <Dialog data-component="CommandDialog" data-location="src/components/ui/shadcn/command.tsx" {...props}>
                <DialogContent className="dw-command-dialog">
                    <DialogTitle className="dw-sr-only">Command Menu</DialogTitle>
                    <Command>{children}</Command>
                </DialogContent>
            </Dialog>
        );
    }
);

const CommandInput = React.forwardRef<React.ComponentRef<typeof CommandInputPrimitive>, React.ComponentPropsWithoutRef<typeof CommandInputPrimitive>>(({ className, ...props }, ref) => (
    <div className="dw-command-input__wrapper" cmdk-input-wrapper="" data-component="CommandInput" data-location="src/components/ui/shadcn/command.tsx">
        <CommandInputPrimitive ref={ref} className={cn("dw-command-input", className)} {...props} />
    </div>
));
CommandInput.displayName = CommandInputPrimitive?.displayName || "CommandInput";

const CommandList = React.forwardRef<React.ComponentRef<typeof CommandListPrimitive>, React.ComponentPropsWithoutRef<typeof CommandListPrimitive>>(({ className, ...props }, ref) => <CommandListPrimitive ref={ref} className={cn("dw-command-list", className)} data-component="CommandList" data-location="src/components/ui/shadcn/command.tsx" {...props} />);
CommandList.displayName = CommandListPrimitive?.displayName || "CommandList";

const CommandEmpty = React.forwardRef<React.ComponentRef<typeof CommandEmptyPrimitive>, React.ComponentPropsWithoutRef<typeof CommandEmptyPrimitive>>(({ className, ...props }, ref) => <CommandEmptyPrimitive ref={ref} className={cn("dw-command-empty", className)} data-component="CommandEmpty" data-location="src/components/ui/shadcn/command.tsx" {...props} />);
CommandEmpty.displayName = CommandEmptyPrimitive?.displayName || "CommandEmpty";

const CommandGroup = React.forwardRef<React.ComponentRef<typeof CommandGroupPrimitive>, React.ComponentPropsWithoutRef<typeof CommandGroupPrimitive>>(({ className, ...props }, ref) => <CommandGroupPrimitive ref={ref} className={cn("dw-command-group", className)} data-component="CommandGroup" data-location="src/components/ui/shadcn/command.tsx" {...props} />);
CommandGroup.displayName = CommandGroupPrimitive?.displayName || "CommandGroup";

const CommandSeparator = React.forwardRef<React.ComponentRef<typeof CommandSeparatorPrimitive>, React.ComponentPropsWithoutRef<typeof CommandSeparatorPrimitive>>(({ className, ...props }, ref) => <CommandSeparatorPrimitive ref={ref} className={cn("dw-command-separator", className)} data-component="CommandSeparator" data-location="src/components/ui/shadcn/command.tsx" {...props} />);
CommandSeparator.displayName = CommandSeparatorPrimitive?.displayName || "CommandSeparator";

const CommandItem = React.forwardRef<React.ComponentRef<typeof CommandItemPrimitive>, React.ComponentPropsWithoutRef<typeof CommandItemPrimitive>>(({ className, ...props }, ref) => <CommandItemPrimitive ref={ref} className={cn("dw-command-item", className)} data-component="CommandItem" data-location="src/components/ui/shadcn/command.tsx" {...props} />);
CommandItem.displayName = CommandItemPrimitive?.displayName || "CommandItem";

const CommandShortcut = memo(
    function CommandShortcut({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
        return <span className={cn("dw-command-shortcut", className)} data-component="CommandShortcut" data-location="src/components/ui/shadcn/command.tsx" {...props} />;
    }
);
CommandShortcut.displayName = "CommandShortcut";

export { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut };
